import {
  useSetSheetTitleExtras,
  useSyncLoading,
  useSyncTitle,
} from "@/context/sheet";
import { T } from "@/i18n";
import { ReactNode } from "react";
import { DeletePrompt } from "../delete-prompt";
import { FieldView } from "../field-view";
import { Button } from "../ui/button";
import { SheetFooter } from "../ui/sheet";
import { Skeleton } from "../ui/skeleton";
import { Pencil } from "lucide-react";

interface DomainViewProps {
  domain: string;
  isLoading: boolean;
  editLink: (children: ReactNode, key: unknown) => JSX.Element;
  title?: string;
  propsList: Array<{ title: string; value: ReactNode; enabled?: boolean }>;
  onDelete?: () => Promise<void> | void;
  children?: ReactNode;
  footerChildren?: ReactNode;
}
export function DomainView({
  domain,
  editLink,
  isLoading,
  title,
  propsList,
  onDelete,
  children,
  footerChildren,
}: DomainViewProps) {
  useSyncLoading(isLoading);
  useSyncTitle(title ?? "-");
  useSetSheetTitleExtras([
    [
      {
        label: `${domain}.contextMenu.edit`,
        icon: () => <Pencil />,
        itemWrapper: ({ children, key }) => editLink(children, key),
      },
    ],
  ]);
  return (
    <>
      <div className="mt-3 grid gap-3">
        {propsList
          .filter(({ enabled }) => typeof enabled === "undefined" || enabled)
          .map(({ title, value }) => (
            <FieldView label={title} key={title}>
              <span>
                {isLoading ? <Skeleton className="h-5 w-10" /> : value}
              </span>
            </FieldView>
          ))}
        {children}
      </div>
      {onDelete || footerChildren ? (
        <SheetFooter className="mt-4">
          {onDelete ? (
            <DeletePrompt
              onProceed={onDelete}
              translations={{
                title: `${domain}.deletePrompt.title`,
                description: `${domain}.deletePrompt.description`,
                continue: `${domain}.deletePrompt.proceed`,
                cancel: `${domain}.deletePrompt.cancel`,
              }}
            >
              <Button type="button" variant="destructive">
                <T tKey={`${domain}.delete.button`} />
              </Button>
            </DeletePrompt>
          ) : (
            <></>
          )}
          {footerChildren}
        </SheetFooter>
      ) : (
        <></>
      )}
    </>
  );
}
